import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Check, Loader2 } from "lucide-react";

const EMPTY = { name: "", phone: "", email: "", quantity: "", message: "", website: "" };

export const QuoteRequestDialog = ({ open, onOpenChange, service }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const close = (o) => { onOpenChange(o); if (!o) setTimeout(() => { setF(EMPTY); setDone(false); }, 300); };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Please add your name");
    if (!f.phone.trim() && !f.email.trim()) return toast.error("Add a phone number or email so we can reply");
    setBusy(true);
    try {
      await api.post("/quotes", { ...f, service: service?.name || "", service_slug: service?.slug || "" });
      setDone(true);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not send — please try again"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[#12141a] border-zinc-800 text-white max-w-md" data-testid="quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Request a quote{service?.name ? ` — ${service.name}` : ""}</DialogTitle>
          <DialogDescription className="text-zinc-400">Tell us what you need and we'll reply by WhatsApp Business or email.</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="py-6 text-center" data-testid="quote-sent">
            <span className="mx-auto w-12 h-12 rounded-full bg-[#4fd23a]/15 text-[#4fd23a] inline-flex items-center justify-center"><Check size={24} /></span>
            <p className="font-display font-bold text-lg mt-3">Got it — thanks {f.name.split(" ")[0]}!</p>
            <p className="text-sm text-zinc-400 mt-1">We'll get back to you with a price shortly.</p>
            <Button onClick={() => close(false)} data-testid="quote-done-btn" className="mt-5 rounded-full bg-white text-black hover:bg-zinc-200">Done</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <input type="text" value={f.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <Input value={f.name} onChange={set("name")} placeholder="Your name *" data-testid="quote-name" className="bg-[#1e222d] border-zinc-700" />
            <div className="grid grid-cols-2 gap-3">
              <Input value={f.phone} onChange={set("phone")} placeholder="Phone / WhatsApp" type="tel" data-testid="quote-phone" className="bg-[#1e222d] border-zinc-700" />
              <Input value={f.email} onChange={set("email")} placeholder="Email" type="email" data-testid="quote-email" className="bg-[#1e222d] border-zinc-700" />
            </div>
            <Input value={f.quantity} onChange={set("quantity")} placeholder="Quantity (e.g. 25 tees)" data-testid="quote-quantity" className="bg-[#1e222d] border-zinc-700" />
            <Textarea value={f.message} onChange={set("message")} rows={4} placeholder="What do you need? Sizes, colours, placement, deadline…" data-testid="quote-message" className="bg-[#1e222d] border-zinc-700" />
            <Button type="submit" disabled={busy} data-testid="quote-submit" className="w-full h-11 rounded-full bg-[#ff6a13] hover:bg-[#ff8a3d] text-white font-semibold">
              {busy ? <Loader2 size={16} className="animate-spin" /> : "Send quote request"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
